import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { collection, getDocs, getDocsFromServer, onSnapshot, query, orderBy, limit, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';

const ProductsContext = createContext(null);
const CACHE_KEY = 'products_cache_v4';
const PAGE_SIZE = 50;
const FULL_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h para no releer todo
const FULL_FLAG_KEY = 'products_full_ts_v4';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

const sanitizeProduct = (p) => ({
  ...p,
  registeredBy:
    typeof p?.registeredBy === 'string'
      ? p.registeredBy
      : (p?.registeredBy?.email ?? p?.registeredBy?.username ?? p?.registeredBy?.uid ?? ''),
  category: typeof p?.category === 'string' ? p.category : (p?.category?.value ?? ''),
  type: String(p?.type ?? 'final'),
  unit: String(p?.unit ?? ''),
  price: Number(p?.price ?? 0),
  costPrice: Number(p?.costPrice ?? 0),
  stock: Number(p?.stock ?? 0),
  excludeFromEntries: !!p?.excludeFromEntries,
});

const timestampToMillis = (value) => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (Number.isFinite(value?.seconds)) {
    return (Number(value.seconds) * 1000) + Math.floor(Number(value.nanoseconds || 0) / 1000000);
  }
  if (Number.isFinite(value)) return Number(value);
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const newestProductTimestamp = (items = []) =>
  items.reduce(
    (newest, item) => Math.max(newest, timestampToMillis(item?.updatedAt || item?.createdAt)),
    0
  );

const dedupeProducts = (items = []) => {
  const map = new Map();
  items.forEach((item) => {
    if (item?.deleted === true) return;
    const key = item?.id || item?.barcode || item?.name;
    if (!key) return;
    map.set(key, item);
  });
  return Array.from(map.values()).sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''), 'es', { sensitivity: 'base' })
  );
};

export const ProductsProvider = ({ children }) => {
  const [products, setProductsState] = useState([]);
  const productsRef = useRef([]);
  const [loading, setLoading] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const loadingAllRef = useRef(false);
  const loadingAllPromiseRef = useRef(null);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const lastDocRef = useRef(null);
  const lastSyncRef = useRef(0);   // timestamp (ms) de la última sync exitosa
  const cacheTsRef = useRef(0);    // timestamp (ms) de guardado de cache
  const fullTsRef = useRef(0);     // timestamp (ms) de último full
  const [fullReady, setFullReady] = useState(false);
  const fullReadyRef = useRef(false);

  // Hidratación inicial desde cache local para que la UI no espere red
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const cacheStr = localStorage.getItem(CACHE_KEY);
      if (cacheStr) {
        const { data, ts, lastSync, complete, count, fullTs } = JSON.parse(cacheStr);
        if (Array.isArray(data)) {
          const next = dedupeProducts(data.map(sanitizeProduct));
          const storedFullTs = Number(fullTs || localStorage.getItem(FULL_FLAG_KEY) || 0);
          const cacheIsComplete = complete === true && Number(count) === next.length && storedFullTs > 0;
          productsRef.current = next;
          setProductsState(next);
          cacheTsRef.current = ts || 0;
          // El cursor siempre sale de marcas de tiempo guardadas por Firestore.
          // Nunca debe depender del reloj local del computador o celular.
          lastSyncRef.current = newestProductTimestamp(next) || Number(lastSync) || 0;
          fullTsRef.current = cacheIsComplete ? storedFullTs : 0;
          fullReadyRef.current = cacheIsComplete;
          setFullReady(cacheIsComplete);
        }
      }
    } catch (err) {
      console.warn('No pude leer cache de productos', err);
    }
  }, []);

  const setProducts = useCallback((updater) => {
    setProductsState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      productsRef.current = next;
      return next;
    });
  }, []);

  const persistCache = useCallback((items = productsRef.current) => {
    try {
      const now = Date.now();
      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: items,
          ts: now,
          lastSync: lastSyncRef.current || 0,
          complete: fullReadyRef.current,
          count: items.length,
          fullTs: fullTsRef.current || 0,
        })
      );
      cacheTsRef.current = now;
    } catch {}
  }, []);

  const loadProducts = useCallback(
    async ({ reset = false, forceFull = false } = {}) => {
      try {
        if (reset) {
          setLoading((prev) => (productsRef.current.length ? prev : true));
          setError(null);
          lastDocRef.current = null;
        } else {
          setLoading(true);
        }

        // 1) Intento de sync incremental por updatedAt (paginar delta)
        const now = Date.now();
        const hasCache = productsRef.current.length > 0;
        const cacheIsFresh = cacheTsRef.current && now - cacheTsRef.current < CACHE_TTL_MS;
        const canDelta = !forceFull && hasCache && lastSyncRef.current;
        if (canDelta) {
          try {
            let cursor = null;
            let more = true;
            let newestSync = Number(lastSyncRef.current || 0);
            while (more) {
              const constraints = [
                // Incluimos el limite para no perder documentos que compartan
                // exactamente la misma marca de tiempo del servidor.
                where('updatedAt', '>=', new Date(lastSyncRef.current)),
                orderBy('updatedAt', 'asc'),
                limit(PAGE_SIZE),
              ];
              if (cursor) constraints.push(startAfter(cursor));
              const snap = await getDocs(query(collection(db, 'productos'), ...constraints));
              const arr = snap.docs.map((d) => sanitizeProduct({ id: d.id, ...d.data() }));
              newestSync = Math.max(newestSync, newestProductTimestamp(arr));

              if (arr.length > 0) {
                setProductsState((prev) => {
                  const map = new Map(prev.map((p) => [p.id, p]));
                  arr.forEach((p) => {
                    if (p.deleted === true) map.delete(p.id);
                    else map.set(p.id, p);
                  });
                  const merged = Array.from(map.values()).sort((a, b) =>
                    String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' })
                  );
                  productsRef.current = merged;
                  persistCache(merged);
                  return merged;
                });
              }

              cursor = snap.docs[snap.docs.length - 1] || null;
              more = snap.size === PAGE_SIZE;
            }
            lastSyncRef.current = newestSync;
            setHasMore(false);
            setLoading(false);
            return;
          } catch (errDelta) {
            console.warn('Fallo sync delta, reintento full:', errDelta);
          }
        }

        // 2) Si el cache está caduco, forzar reset true para rehidratar ordenado
        const needsFull = forceFull || !hasCache || !cacheIsFresh || reset;

        // 2a. Consulta ordenada (full o paginada)
        let constraints = [orderBy('name'), limit(PAGE_SIZE)];
        if (!reset && lastDocRef.current) {
          constraints = [orderBy('name'), startAfter(lastDocRef.current), limit(PAGE_SIZE)];
        }

        const snap = await getDocs(query(collection(db, 'productos'), ...constraints));
        const arr = snap.docs.map((d) => sanitizeProduct({ id: d.id, ...d.data() }));
        lastSyncRef.current = Math.max(lastSyncRef.current, newestProductTimestamp(arr));

        setProductsState((prev) => {
          const merged = needsFull ? dedupeProducts(arr) : dedupeProducts([...prev, ...arr]);
          productsRef.current = merged;
          persistCache(merged);
          return merged;
        });

        const last = snap.docs[snap.docs.length - 1] || null;
        lastDocRef.current = last;
        setHasMore(snap.docs.length === PAGE_SIZE);
      } catch (err) {
        console.error('getDocs productos (context)', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [persistCache]
  );

  const loadAllProductsOnce = useCallback(async ({ force = false } = {}) => {
    if (loadingAllRef.current) return loadingAllPromiseRef.current;
    const now = Date.now();
    const fullTs = fullTsRef.current || Number(localStorage.getItem(FULL_FLAG_KEY) || 0);
    if (!force && fullReadyRef.current && fullTs && now - fullTs < FULL_CACHE_TTL_MS) {
      setFullReady(true);
      setHasMore(false);
      return;
    }

    loadingAllRef.current = true;
    setLoadingAll(true);
    const request = (async () => {
      try {
        // Una sola lectura autoritativa evita marcar como completo un paginado parcial.
        const snap = await getDocsFromServer(collection(db, 'productos'));
        const next = dedupeProducts(
          snap.docs.map((d) => sanitizeProduct({ id: d.id, ...d.data() }))
        );

        productsRef.current = next;
        lastSyncRef.current = newestProductTimestamp(next);
        fullTsRef.current = Date.now();
        fullReadyRef.current = true;
        localStorage.setItem(FULL_FLAG_KEY, String(fullTsRef.current));
        setProductsState(next);
        setFullReady(true);
        setHasMore(false);
        persistCache(next);
      } catch (err) {
        // Sin red conservamos el ultimo catalogo completo conocido.
        console.error('No se pudo verificar el catalogo completo', err);
        if (!fullReadyRef.current) setFullReady(false);
      } finally {
        loadingAllRef.current = false;
        loadingAllPromiseRef.current = null;
        setLoadingAll(false);
        lastDocRef.current = null;
      }
    })();

    loadingAllPromiseRef.current = request;
    return request;
  }, [persistCache]);

  const refreshProducts = useCallback(
    ({ forceFull = false } = {}) => {
      const now = Date.now();
      const hasCache = productsRef.current.length > 0;
      const cacheExpired = !cacheTsRef.current || now - cacheTsRef.current > CACHE_TTL_MS;
      const fullTs = fullTsRef.current || Number(localStorage.getItem(FULL_FLAG_KEY) || 0);
      const fullFresh = fullTs && now - fullTs < FULL_CACHE_TTL_MS;

      if (forceFull) {
        return loadAllProductsOnce({ force: true });
      }

      // Cache corto vencido pero full fresco: no resetear a 50, solo delta
      if (hasCache && fullReadyRef.current && cacheExpired && fullFresh) {
        return loadProducts({ reset: false, forceFull: false });
      }

      if (!hasCache || !fullReadyRef.current || !fullFresh) {
        return loadAllProductsOnce();
      }

      // Cache vigente: delta normal
      return loadProducts({ reset: false, forceFull: false });
    },
    [loadProducts, loadAllProductsOnce]
  );

  const subscribeToProductChanges = useCallback(() => {
    const syncFrom = Number(lastSyncRef.current || 0);
    const hasReliableCursor =
      fullReadyRef.current && syncFrom > 0 && productsRef.current.length > 0;
    const listeningFullCatalog = !hasReliableCursor;

    // Con cache escuchamos solo el delta. En un equipo nuevo escuchamos la
    // coleccion completa para cubrir tambien los cambios durante la carga.
    const changesQuery = hasReliableCursor
      ? query(
          collection(db, 'productos'),
          where('updatedAt', '>=', new Date(syncFrom)),
          orderBy('updatedAt', 'asc')
        )
      : collection(db, 'productos');

    return onSnapshot(
      changesQuery,
      (snapshot) => {
        const changes = snapshot.docChanges();
        let newestSync = syncFrom;

        changes.forEach((change) => {
          const updatedMillis = timestampToMillis(
            change.doc.data()?.updatedAt || change.doc.data()?.createdAt
          );
          if (Number.isFinite(updatedMillis)) newestSync = Math.max(newestSync, updatedMillis);
        });
        lastSyncRef.current = newestSync;

        // Una respuesta completa confirmada por el servidor repara por si sola
        // cualquier cache local viejo, truncado o corrupto.
        if (listeningFullCatalog && !snapshot.metadata.fromCache) {
          const next = dedupeProducts(
            snapshot.docs.map((item) => sanitizeProduct({ id: item.id, ...item.data() }))
          );
          lastSyncRef.current = newestProductTimestamp(next);
          fullTsRef.current = Date.now();
          fullReadyRef.current = true;
          localStorage.setItem(FULL_FLAG_KEY, String(fullTsRef.current));
          productsRef.current = next;
          setProductsState(next);
          setFullReady(true);
          setHasMore(false);
          persistCache(next);
          return;
        }

        setProductsState((previous) => {
          const byId = new Map(previous.map((product) => [product.id, product]));

          changes.forEach((change) => {
            if (change.type === 'removed') {
              byId.delete(change.doc.id);
              return;
            }

            const product = sanitizeProduct({ id: change.doc.id, ...change.doc.data() });
            if (product.deleted === true) {
              byId.delete(product.id);
            } else {
              byId.set(product.id, product);
            }
          });

          const next = dedupeProducts(Array.from(byId.values()));
          productsRef.current = next;
          persistCache(next);
          return next;
        });
      },
      (syncError) => {
        console.warn('No se pudo mantener la sincronización incremental de productos', syncError);
      }
    );
  }, [persistCache]);

  const loadMoreProducts = useCallback(() => {
    if (!hasMore || loading) return;
    return loadProducts({ reset: false, forceFull: true });
  }, [hasMore, loading, loadProducts]);

  const value = useMemo(
    () => ({
      products,
      setProducts,
      loading,
      loadingAll,
      fullReady,
      error,
      hasMore,
      refreshProducts,
      subscribeToProductChanges,
      loadMoreProducts,
      loadAllProductsOnce,
    }),
    [products, loading, loadingAll, fullReady, error, hasMore, refreshProducts, subscribeToProductChanges, loadMoreProducts, loadAllProductsOnce]
  );

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const useProducts = () => {
  const ctx = useContext(ProductsContext);
  if (!ctx) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return ctx;
};
